// app/javascript/components/quotes/builder/components/AddBar.js
// Bottom toolbar of the project quote builder: a row of buttons to add any
// top-level element (Lot, Ouvrage, Composant with its category, Article).
// Non-lot elements are appended to the last lot; if there is none yet, a lot is
// created to hold them (the serializer only persists lines inside a group).
import React, { useEffect, useRef, useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { useQuoteEditor } from "../context/QuoteEditorContext"
import { useGroupPicker } from "../context/GroupPickerContext"
import AddArticleModal from "./AddArticleModal"
import IconBadge from "@react/shared/ui/IconBadge"
import { COST_CATEGORIES, costCategoryVisual } from "@react/quotes/qline_service_blueprints/kinds"
import { makeLine as newLine } from "../helpers/newLine"
import { lineHolderUid } from "../helpers/treeMath"

const AddBar = () => {
  const { state, dispatch }       = useQuoteEditor()
  const { open: openGroupPicker } = useGroupPicker()

  const sections    = (state.sections || []).filter(s => !s._destroy)
  const lastSection = sections[sections.length - 1]

  // "Add" places its line where the user works, not at the end of the quote.
  // On opening nobody has touched anything yet, so the target is the last
  // section.
  const stillExists = uid => {
    if (!uid) return false
    const seek = nodes => (nodes || []).some(n =>
      (n._uid === uid && !n._destroy) || seek(n.children))
    return seek(state.sections)
  }
  const activeUid = stillExists(state.activeSectionUid) ? state.activeSectionUid : null
  const lastUid   = activeUid || lastSection?._uid
  const lineUid   = lastUid ? lineHolderUid(state.sections, lastUid) : null

  const [kindOpen, setKindOpen]         = useState(false)
  const [articleOpen, setArticleOpen]   = useState(false)
  const [pendingOuvrage, setPendingOuvr]= useState(false)
  const kindRef = useRef(null)

  // Close the category dropdown on outside click / Escape.
  useEffect(() => {
    if (!kindOpen) return
    const onClick = e => { if (!kindRef.current?.contains(e.target)) setKindOpen(false) }
    const onKey   = e => { if (e.key === "Escape") setKindOpen(false) }
    document.addEventListener("click", onClick, true)
    document.addEventListener("keydown", onKey)
    return () => { document.removeEventListener("click", onClick, true); document.removeEventListener("keydown", onKey) }
  }, [kindOpen])

  // Ouvrage with no lot yet: create a lot, then open the library for it once it exists.
  useEffect(() => {
    if (pendingOuvrage && lineUid) { openGroupPicker(lineUid); setPendingOuvr(false) }
  }, [pendingOuvrage, lineUid, openGroupPicker])

  const addLot = () => dispatch({
    type: "ADD_SECTION",
    payload: { type: "group", description: "", children: [], _expanded: true },
  })

  const makeLine = costCategory => newLine(state, costCategory)

  const makeArticleLine = data => ({
    ...makeLine(data.cost_category || "materials"),
    description:         data.name || "",
    unit:                data.unit || "",
    unit_price:          data.sale_price ?? 0,
    purchase_price:      data.purchase_price ?? null,
    ref_product_distrib: data.ref_product_brand || "",
    product_id:          data.id,
  })

  // Append a line to the last lot, or wrap it in a fresh lot (atomic — assignUids
  // recurses into children).
  const addLine = line => {
    if (lineUid) {
      dispatch({ type: "ADD_LINES_TO_SECTION", payload: { sectionUid: lineUid, lines: [line], focusLast: true } })
    } else {
      dispatch({ type: "ADD_SECTION", payload: { type: "group", description: "", children: [line], _expanded: true } })
    }
  }

  const addComposant = kind => { setKindOpen(false); addLine(makeLine(kind)) }
  const addArticle   = data => addLine(makeArticleLine(data))
  // A lot holding sub-lots takes no ouvrage: it goes to its last sub-lot, as a line does.
  const addOuvrage   = () => { if (lineUid) openGroupPicker(lineUid); else { addLot(); setPendingOuvr(true) } }
  const addDescription = () => {
    if (lineUid) {
      dispatch({ type: "ADD_DESCRIPTION", payload: { parentUid: lineUid } })
    } else {
      const description = { type: "descriptive", description: "" }
      dispatch({ type: "ADD_SECTION", payload: { type: "group", description: "", children: [description], _expanded: true } })
    }
  }
  const enabled     = state.account?.enabled_cost_categories
  const allowedKeys = (Array.isArray(enabled) && enabled.length > 0) ? enabled : COST_CATEGORIES.map(c => c.value)
  const kinds       = COST_CATEGORIES.filter(c => allowedKeys.includes(c.value))

  const primary   = "inline-flex items-center gap-2 h-9 px-3.5 rounded-lg bg-brand-500 text-white text-[13px] font-semibold hover:bg-brand-600 transition-colors"
  const secondary = "inline-flex items-center gap-2 h-9 px-3 rounded-lg border border-slate-200 bg-white text-slate-700 text-[13px] font-medium hover:border-brand-400 hover:text-brand-600 transition-colors"

  return (
    <div className="p-3 shrink-0">
      <div className="flex flex-wrap items-center justify-end gap-2 rounded-xl border border-slate-200 bg-slate-50/70 p-2.5">
        <span className="pl-1 pr-0.5 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
          {I18n.t("quotes.builder.toolbar.add")}
        </span>

        <button type="button" onClick={addLot} className={primary}>
          <FontAwesomeIcon icon="layer-group" /> {I18n.t("quotes.builder.col_head.add_group")}
        </button>

        <span className="mx-0.5 h-5 w-px bg-slate-200" />

        <button type="button" onClick={addOuvrage} className={secondary}>
          <FontAwesomeIcon icon="cubes" /> {I18n.t("quotes.builder.add_group_modal.title_detail_fallback")}
        </button>

        {/* Composant + category dropdown (opens upward — the bar sits at the bottom) */}
        <div className="relative" ref={kindRef}>
          <button type="button" onClick={() => setKindOpen(o => !o)} className={secondary} aria-expanded={kindOpen}>
            <FontAwesomeIcon icon="screwdriver-wrench" /> {I18n.t("quotes.builder.toolbar.composant")}
            <FontAwesomeIcon icon="chevron-down" className={`!w-2.5 text-slate-400 transition-transform ${kindOpen ? "rotate-180" : ""}`} />
          </button>
          {kindOpen && (
            <div className="absolute bottom-full left-0 mb-1.5 min-w-[200px] bg-white border border-slate-200 rounded-[10px] p-1.5 shadow-lg z-[1200] font-sans text-[13px]">
              {kinds.map(category => {
                const visual = costCategoryVisual(category.value)
                return (
                  <button
                    key       = {category.value}
                    type      = "button"
                    onClick   = {() => addComposant(category.value)}
                    className = "w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-md bg-transparent border-0 cursor-pointer text-[13px] font-medium text-left text-slate-700 hover:bg-slate-100 hover:text-slate-900"
                  >
                    <IconBadge tone={visual.tone} size="sm"><FontAwesomeIcon icon={visual.icon} /></IconBadge>
                    {category.label}
                  </button>
                )
              })}
            </div>
          )}
        </div>

        <button type="button" onClick={() => setArticleOpen(true)} className={secondary}>
          <FontAwesomeIcon icon="box" /> {I18n.t("quotes.builder.toolbar.article")}
        </button>

        <button
          type      = "button"
          onClick   = {addDescription}
          title     = {I18n.t("quotes.builder.description.toolbar_hint")}
          className = {secondary}
        >
          <FontAwesomeIcon icon="align-left" /> {I18n.t("quotes.builder.toolbar.description")}
        </button>
      </div>

      <AddArticleModal
        isOpen   = {articleOpen}
        onClose  = {() => setArticleOpen(false)}
        clientId = {state.client?.id}
        onSelect = {addArticle}
      />
    </div>
  )
}

export default AddBar
